import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { useAuth } from '@/features/auth/authContext';
import { isSocketReady, releaseSocket, retainSocket } from '@/services/wsClient';
import {
  sessionScopeKey,
  useSessionScopeFence,
  type SessionScopeIdentity,
} from './useSessionScope';
import {
  realtimeClient,
  type ProjectMapResponse,
  type RealtimeEventItem,
} from '@/services/realtimeClient';

const EVENT_LIMIT = 30;

export type SocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export const realtimeQueryKeys = {
  events: (identity: SessionScopeIdentity, limit = EVENT_LIMIT) =>
    [
      'realtime',
      'tenant',
      identity.tenantId,
      'user',
      identity.userId,
      ...sessionScopeKey(identity),
      'events',
      limit,
    ] as const,
  projectMap: (identity: SessionScopeIdentity) =>
    [
      'realtime',
      'tenant',
      identity.tenantId,
      'user',
      identity.userId,
      ...sessionScopeKey(identity),
      'project-map-cohorts-v1',
    ] as const,
};

export function useRealtimeDashboard() {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const { identity, enabled: sessionEnabled, isCurrent } = useSessionScopeFence();
  const { tenantId, userId } = identity;
  const eventsEnabled =
    sessionEnabled && hasPermission(auth.permissions, PERMISSIONS.DASHBOARD_READ);
  const mapEnabled = sessionEnabled && hasPermission(auth.permissions, PERMISSIONS.PROJECTS_READ);
  const enabled = eventsEnabled || mapEnabled;
  const eventsKey = useMemo(() => realtimeQueryKeys.events(identity), [identity]);
  const mapKey = useMemo(() => realtimeQueryKeys.projectMap(identity), [identity]);
  const [socketStatus, setSocketStatus] = useState<SocketStatus>(
    enabled ? 'connecting' : 'disconnected',
  );

  const eventsQuery = useQuery<RealtimeEventItem[]>({
    queryKey: eventsKey,
    queryFn: async ({ signal }) => {
      if (!eventsEnabled || !isCurrent(identity))
        throw new Error('Current event access is required.');
      const result = await realtimeClient.getEvents(EVENT_LIMIT, {
        signal,
        sessionOrigin: identity.sessionOrigin,
        allowRefresh: false,
      });
      signal.throwIfAborted();
      if (!isCurrent(identity)) throw new Error('Event access changed while loading.');
      return result;
    },
    enabled: eventsEnabled,
    placeholderData: () => undefined,
    retry: false,
    staleTime: 15_000,
  });

  const projectMapQuery = useQuery<ProjectMapResponse>({
    queryKey: mapKey,
    queryFn: async ({ signal }) => {
      if (!mapEnabled || !isCurrent(identity)) throw new Error('Current map access is required.');
      const result = await realtimeClient.getProjectMap({
        signal,
        sessionOrigin: identity.sessionOrigin,
        allowRefresh: false,
      });
      signal.throwIfAborted();
      if (!isCurrent(identity)) throw new Error('Map access changed while loading.');
      return result;
    },
    enabled: mapEnabled,
    placeholderData: () => undefined,
    retry: false,
    staleTime: 15_000,
  });

  useEffect(() => {
    if (!enabled) {
      setSocketStatus('disconnected');
      return;
    }

    setSocketStatus('connecting');
    let active = true;
    const socket = retainSocket();
    const current = () => active && isCurrent(identity);
    const permissions = JSON.parse(identity.permissions) as string[];

    const refreshLiveQueries = () => {
      if (!current()) return;
      void queryClient.invalidateQueries({
        queryKey: eventsKey,
        exact: true,
        refetchType: 'active',
      });
      void queryClient.invalidateQueries({
        queryKey: mapKey,
        exact: true,
        refetchType: 'active',
      });
      void queryClient.invalidateQueries({
        queryKey: ['dashboard', 'command-center'],
        refetchType: 'active',
      });
    };

    const handleTransportConnect = () => {
      if (current()) setSocketStatus('connecting');
    };
    const handleReady = () => {
      if (!current()) return;
      setSocketStatus('connected');
      for (const [permission, topic] of [
        [PERMISSIONS.DASHBOARD_READ, 'realtime'],
        [PERMISSIONS.PROJECTS_READ, 'projects.projects'],
        [PERMISSIONS.ESTIMATING_LEADS_READ, 'estimating.leads'],
        [PERMISSIONS.ESTIMATING_ESTIMATES_READ, 'estimating.estimates'],
      ] as const) {
        if (hasPermission(permissions, permission)) socket.emit('realtime.subscribe', { topic });
      }
      refreshLiveQueries();
    };
    const handleDisconnect = () => {
      if (current()) setSocketStatus('disconnected');
    };
    const handleError = () => {
      if (current()) setSocketStatus('error');
    };
    const handleEvent = (event: RealtimeEventItem) => {
      if (
        !current() ||
        !event?.id ||
        event.tenantId !== tenantId ||
        (event.permission !== null && !hasPermission(permissions, event.permission)) ||
        (event.recipientUserId !== null && event.recipientUserId !== userId)
      ) {
        return;
      }

      if (eventsEnabled) {
        queryClient.setQueryData<RealtimeEventItem[]>(eventsKey, (events = []) => {
          const withoutDuplicate = events.filter((item) => item.id !== event.id);
          return [event, ...withoutDuplicate].slice(0, EVENT_LIMIT);
        });
      }

      if (
        event.eventName === 'dashboard.metric.updated' ||
        event.entityType === 'Project' ||
        event.entityType === 'Lead' ||
        event.entityType === 'Estimate' ||
        event.entityType === 'EstimatePackage' ||
        event.topic === 'projects.projects'
      ) {
        void queryClient.invalidateQueries({
          queryKey: mapKey,
          exact: true,
          refetchType: 'active',
        });
        void queryClient.invalidateQueries({
          queryKey: ['dashboard', 'command-center'],
          refetchType: 'active',
        });
      }
      if (
        event.eventName === 'notification.created' ||
        (event.eventName === 'dashboard.metric.updated' &&
          Boolean(
            event.payload &&
              typeof event.payload === 'object' &&
              'metric' in event.payload &&
              (event.payload as { metric?: unknown }).metric === 'notifications.unread',
          ))
      ) {
        void queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === 'settings',
          refetchType: 'active',
        });
      }
    };

    socket.on('connect', handleTransportConnect);
    socket.on('realtime.connected', handleReady);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleError);
    socket.on('realtime.error', handleError);
    socket.on('realtime.event', handleEvent);

    if (isSocketReady()) {
      handleReady();
    }

    return () => {
      active = false;
      socket.off('connect', handleTransportConnect);
      socket.off('realtime.connected', handleReady);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleError);
      socket.off('realtime.error', handleError);
      socket.off('realtime.event', handleEvent);
      releaseSocket();
    };
  }, [
    enabled,
    eventsEnabled,
    eventsKey,
    mapKey,
    queryClient,
    tenantId,
    userId,
    identity,
    isCurrent,
  ]);

  return {
    socketStatus: enabled ? socketStatus : ('disconnected' as const),
    eventsQuery: {
      ...eventsQuery,
      data:
        eventsEnabled && isCurrent(identity) && !eventsQuery.isError ? eventsQuery.data : undefined,
    },
    projectMapQuery: {
      ...projectMapQuery,
      data:
        mapEnabled && isCurrent(identity) && !projectMapQuery.isError
          ? projectMapQuery.data
          : undefined,
    },
  };
}
